/* The table pane's top row, level with the rail's New table: search, filters, hidden columns
   and Add column on the left; the Table / Schema switch on the right. Filters that are on sit
   under it as removable chips. */
import React from "react";
import { EyeOff, ListFilter, Network, Plus, Table2, X } from "lucide-react";

import { GhostButton, IconButton, RemovableChip, SearchInput } from "@/shared/components/ui-kit";
import { menuItem, menuLabel } from "@/shared/components/menuStyles";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { inputCls } from "@/shared/components/task-atoms";
import { cn } from "@/shared/lib/cn";

import { keptOnRow } from "./table/columns";

import type { SpaceColumn, SpaceCondition } from "@/shared/types";

export type SpaceView = "table" | "schema";

const OPS: { op: SpaceCondition["op"]; words: string; needsValue: boolean }[] = [
  { op: "is", words: "is", needsValue: true },
  { op: "is_not", words: "is not", needsValue: true },
  { op: "contains", words: "contains", needsValue: true },
  { op: "above", words: "is above", needsValue: true },
  { op: "below", words: "is below", needsValue: true },
  { op: "empty", words: "is empty", needsValue: false },
  { op: "not_empty", words: "is not empty", needsValue: false },
];
const fieldCls = cn(inputCls, "h-7 px-2 text-[12.5px]");  // the app's small input, a size down


export function ViewSwitch({ view, onView }: { view: SpaceView; onView: (view: SpaceView) => void }) {
  return (
    <div className="flex gap-0.5 p-0.5 rounded-[8px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] shrink-0">
      {([["table", "Table", Table2], ["schema", "Schema", Network]] as const).map(([key, label, Icon]) => (
        <button
          key={key}
          type="button"
          aria-pressed={view === key}
          onClick={() => onView(key)}
          className={cn(
            "press h-[26px] px-2.5 rounded-[6px] text-[12.5px] font-medium inline-flex items-center gap-1.5",
            view === key
              ? "bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline)] shadow-lift"
              : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]",
          )}
        >
          <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
          <span className="hidden xl:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}

export default function TableToolbar({
  columns, search, onSearch, conditions, onConditions, hidden, onHidden, onAddColumn, onView, narrow = false,
}: {
  columns: SpaceColumn[];
  search: string;
  onSearch: (search: string) => void;
  conditions: SpaceCondition[];
  onConditions: (conditions: SpaceCondition[]) => void;
  hidden: string[];
  onHidden: (hidden: string[]) => void;
  /** Absent on People: its columns are Definable's. */
  onAddColumn?: () => void;
  onView: (view: SpaceView) => void;
  /** A row is open beside the table: buttons keep their icons only. */
  narrow?: boolean;
}) {
  const named = (key: string) => columns.find((column) => column.key === key)?.name ?? key;
  const set = (index: number, patch: Partial<SpaceCondition>) =>
    onConditions(conditions.map((condition, at) => (at === index ? { ...condition, ...patch } : condition)));

  return (
    <div className="shrink-0 border-b hairline">
      <div className="h-12 flex items-center gap-2 pl-4 sm:pl-5 pr-4 min-w-0">
        <SearchInput value={search} onChange={(e) => onSearch(e.target.value)} placeholder="Search rows" aria-label="Search rows" className={cn("min-w-[110px] shrink max-sm:w-[120px]", narrow ? "w-[160px]" : "w-[240px]")} />
        <Popover>
          <PopoverTrigger asChild>
            <GhostButton className="h-7 px-2.5 shrink-0">
              <ListFilter className="w-3.5 h-3.5" strokeWidth={1.75} />
              {!narrow && <span className="hidden sm:inline">Filter</span>}
              {conditions.length > 0 && (
                <span className="min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold tabular-nums bg-[var(--brand)] text-[var(--brand-foreground)] grid place-items-center">
                  {conditions.length}
                </span>
              )}
            </GhostButton>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[min(440px,calc(100vw-32px))] p-2 rounded-[14px] border border-[var(--hairline-strong)] bg-[var(--surface)] shadow-menu space-y-1.5">
            {conditions.length === 0 && <p className="m-0 px-1 py-1 text-[12.5px] text-[var(--text-tertiary)]">No filters: every row shows.</p>}
            {conditions.map((condition, index) => {
              const op = OPS.find((entry) => entry.op === condition.op) ?? OPS[0];
              return (
                <div key={index} className="flex items-center gap-1.5">
                  <select aria-label="Column" value={condition.column} onChange={(e) => set(index, { column: e.target.value })} className={cn(fieldCls, "w-[130px]")}>
                    {columns.filter(keptOnRow).map((column) => <option key={column.key} value={column.key}>{column.name}</option>)}
                  </select>
                  <select aria-label="How" value={condition.op} onChange={(e) => set(index, { op: e.target.value as SpaceCondition["op"] })} className={cn(fieldCls, "w-[110px]")}>
                    {OPS.map((entry) => <option key={entry.op} value={entry.op}>{entry.words}</option>)}
                  </select>
                  {op.needsValue && (
                    <input aria-label="Value" value={String(condition.value ?? "")} onChange={(e) => set(index, { value: e.target.value })} className={cn(fieldCls, "flex-1 min-w-0")} />
                  )}
                  <IconButton aria-label="Remove filter" onClick={() => onConditions(conditions.filter((_, at) => at !== index))}>
                    <X className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </IconButton>
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => onConditions([...conditions, { column: columns.find(keptOnRow)?.key ?? "", op: "is", value: "" }])}
              className="press inline-flex items-center gap-1.5 h-7 px-2 rounded-[8px] text-[12.5px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] hover:text-[var(--text-primary)]"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.75} /> Add a filter
            </button>
          </PopoverContent>
        </Popover>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <GhostButton className="h-7 px-2.5 shrink-0">
              <EyeOff className="w-3.5 h-3.5" strokeWidth={1.75} />
              {!narrow && <span className="hidden lg:inline">{hidden.length ? `${hidden.length} hidden` : "Columns"}</span>}
            </GhostButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-52 p-1 rounded-[12px] bg-[var(--surface)] border border-[var(--hairline-strong)] shadow-menu">
            <DropdownMenuLabel className={menuLabel}>Show columns</DropdownMenuLabel>
            {columns.slice(1).map((column) => (
              <DropdownMenuCheckboxItem
                key={column.key}
                checked={!hidden.includes(column.key)}
                onSelect={(event) => event.preventDefault()}
                onCheckedChange={(shown) => onHidden(shown ? hidden.filter((key) => key !== column.key) : [...hidden, column.key])}
                className={menuItem}
              >
                {column.name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {onAddColumn && (
          <GhostButton onClick={onAddColumn} className="h-7 px-2.5 shrink-0">
            <Plus className="w-3.5 h-3.5" strokeWidth={1.75} />
            {!narrow && <span className="hidden lg:inline">Add column</span>}
          </GhostButton>
        )}
        <div className="flex-1" />
        <ViewSwitch view="table" onView={onView} />
      </div>
      {conditions.some((condition) => condition.column) && (
        <div className="flex flex-wrap items-center gap-1.5 px-5 pb-2.5 -mt-0.5">
          {conditions.map((condition, index) => (
            <RemovableChip key={index} removeLabel="Remove filter" onRemove={() => onConditions(conditions.filter((_, at) => at !== index))}>
              {named(condition.column)} {OPS.find((entry) => entry.op === condition.op)?.words}
              {condition.value !== undefined && condition.value !== "" ? ` ${String(condition.value)}` : ""}
            </RemovableChip>
          ))}
        </div>
      )}
    </div>
  );
}
